import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { BlogHeader } from '@/components/BlogHeader';
import { CTABanner } from '@/components/CTABanner';
import { PostHero } from '@/components/PostHero';
import { ReadingProgress } from '@/components/ReadingProgress';
import { RelatedPosts } from '@/components/RelatedPosts';
import { SocialShare } from '@/components/SocialShare';
import { InlineCTA } from '@/components/InlineCTA';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useKopfaiClickTracking } from '@/hooks/useKopfaiClickTracking';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { ArrowLeft, Calendar, Eye, Clock, Home } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet';

const SITE_URL = "https://blog.kopfai.com";

interface Post {
  id: number;
  title: string;
  slug: string;
  meta_description: string;
  keywords: string[];
  category: string;
  content: string;
  created_at: string;
  updated_at?: string;
  views: number;
  cover_image_url?: string;
}

export default function Post() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const { trackClick } = useKopfaiClickTracking();

  useEffect(() => {
    if (slug) {
      loadPost(slug);
    }
  }, [slug]);

  // Track clicks on KopfAI content links
  useEffect(() => {
    if (!post) return;

    const handleKopfaiLinkClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('kopfai-content-link')) {
        trackClick('article_content_link', post.id, 'KopfAI');
      }
    };

    document.addEventListener('click', handleKopfaiLinkClick);

    return () => {
      document.removeEventListener('click', handleKopfaiLinkClick);
    };
  }, [post, trackClick]);

  const loadPost = async (postSlug: string) => {
    try {
      setLoading(true);
      
      // Buscar post
      const { data, error } = await supabase
        .from('posts')
        .select('*')
        .eq('slug', postSlug)
        .maybeSingle();

      if (error) throw error;
      
      if (!data) {
        toast.error('Post não encontrado');
        return;
      }

      setPost(data);

      // Incrementar views
      await supabase
        .from('posts')
        .update({ views: data.views + 1 })
        .eq('id', data.id);

    } catch (error) {
      console.error('Erro ao carregar post:', error);
      toast.error('Erro ao carregar post');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-kopf-white pb-32">
        <BlogHeader />
        <main className="container mx-auto px-4 py-8 max-w-4xl">
          <Skeleton className="h-8 w-32 mb-6" />
          <Skeleton className="h-12 w-3/4 mb-4" />
          <Skeleton className="h-6 w-1/2 mb-8" />
          <div className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </main>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-kopf-white pb-32">
        <BlogHeader />
        <main className="container mx-auto px-4 py-8 max-w-4xl text-center">
          <h1 className="text-2xl font-bold text-kopf-primary mb-4">Post não encontrado</h1>
          <Button asChild>
            <Link to="/">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar ao Blog
            </Link>
          </Button>
        </main>
      </div>
    );
  }

  const calculateReadingTime = (content: string) => {
    const wordsPerMinute = 200;
    const text = content.replace(/<[^>]*>/g, '');
    const words = text.split(/\s+/).length;
    return Math.ceil(words / wordsPerMinute);
  };

  // Extract FAQ items from content (looking for h2/h3 questions followed by paragraphs)
  const extractFAQFromContent = (content: string): Array<{ question: string; answer: string }> => {
    const faqItems: Array<{ question: string; answer: string }> = [];
    
    // Match headings that end with "?" followed by content
    const questionPattern = /<h[23][^>]*>([^<]*\?)<\/h[23]>\s*(<p[^>]*>[\s\S]*?<\/p>)/gi;
    let match;
    
    while ((match = questionPattern.exec(content)) !== null) {
      const question = match[1].trim();
      const answerHtml = match[2];
      const answer = answerHtml.replace(/<[^>]*>/g, '').trim();
      
      if (question && answer && answer.length > 20) {
        faqItems.push({ question, answer: answer.substring(0, 500) });
      }
    }
    
    return faqItems.slice(0, 5); // Max 5 FAQ items
  };

  // Extract HowTo steps from content (looking for numbered lists or h2/h3 with step patterns)
  const extractHowToFromContent = (content: string, title: string): { name: string; steps: Array<{ name: string; text: string }> } | null => {
    // Only extract HowTo if title suggests it's a how-to article
    const howToKeywords = ['como', 'passo', 'guia', 'tutorial', 'estratégia', 'exercício', 'técnica', 'dica'];
    const titleLower = title.toLowerCase();
    const isHowTo = howToKeywords.some(kw => titleLower.includes(kw));
    
    if (!isHowTo) return null;

    const steps: Array<{ name: string; text: string }> = [];
    
    // Match numbered headings (1., 2., etc.) or "Passo X" patterns
    const stepPattern = /<h[23][^>]*>(?:\d+[\.\)]\s*|Passo\s+\d+[:\s]*)?([^<]+)<\/h[23]>\s*(<p[^>]*>[\s\S]*?<\/p>)/gi;
    let match;
    
    while ((match = stepPattern.exec(content)) !== null) {
      const stepName = match[1].trim();
      const stepText = match[2].replace(/<[^>]*>/g, '').trim();
      
      if (stepName && stepText && stepText.length > 20) {
        steps.push({ name: stepName, text: stepText.substring(0, 300) });
      }
    }
    
    if (steps.length >= 2) {
      return { name: title, steps: steps.slice(0, 8) };
    }
    
    return null;
  };

  const readingTime = calculateReadingTime(post.content);
  const postUrl = `${SITE_URL}/post/${post.slug}`;
  const publishedDate = new Date(post.created_at).toISOString();
  const modifiedDate = post.updated_at ? new Date(post.updated_at).toISOString() : publishedDate;
  const coverImage = post.cover_image_url || `${SITE_URL}/og-default.png`;
  const wordCount = post.content.replace(/<[^>]*>/g, '').split(/\s+/).length;

  // Extract structured content
  const faqItems = extractFAQFromContent(post.content);
  const howToData = extractHowToFromContent(post.content, post.title);

  // JSON-LD Structured Data for BlogPosting with enhanced SEO
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.meta_description,
    "image": {
      "@type": "ImageObject",
      "url": coverImage,
      "width": 1200,
      "height": 630
    },
    "author": {
      "@type": "Organization",
      "name": "KopfAI",
      "url": "https://kopfai.com",
      "logo": {
        "@type": "ImageObject",
        "url": `${SITE_URL}/logo.png`
      }
    },
    "publisher": {
      "@type": "Organization",
      "name": "KopfAI Blog",
      "logo": {
        "@type": "ImageObject",
        "url": `${SITE_URL}/logo.png`,
        "width": 200,
        "height": 60
      }
    },
    "datePublished": publishedDate,
    "dateModified": modifiedDate,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": postUrl
    },
    "keywords": post.keywords.join(", "),
    "articleSection": post.category,
    "wordCount": wordCount,
    "timeRequired": `PT${readingTime}M`,
    "isAccessibleForFree": true,
    "inLanguage": "pt-BR",
    "speakable": {
      "@type": "SpeakableSpecification",
      "cssSelector": [".prose-kopf h2", ".prose-kopf p:first-of-type"]
    }
  };

  // JSON-LD for BreadcrumbList
  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Início",
        "item": SITE_URL
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": post.category,
        "item": `${SITE_URL}/?category=${encodeURIComponent(post.category)}`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": post.title,
        "item": postUrl
      }
    ]
  };

  // JSON-LD for FAQPage (if FAQ content detected)
  const faqJsonLd = faqItems.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  } : null;

  // JSON-LD for HowTo (if how-to content detected)
  const howToJsonLd = howToData ? {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": howToData.name,
    "description": post.meta_description,
    "image": coverImage,
    "totalTime": `PT${readingTime}M`,
    "step": howToData.steps.map((step, index) => ({
      "@type": "HowToStep",
      "position": index + 1,
      "name": step.name,
      "text": step.text
    }))
  } : null;

  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>{post.title} | KopfAI Blog</title>
        <meta name="description" content={post.meta_description} />
        <meta name="keywords" content={post.keywords.join(', ')} />
        <meta name="author" content="KopfAI" />
        <link rel="canonical" href={postUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={postUrl} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.meta_description} />
        <meta property="og:image" content={coverImage} />
        <meta property="og:site_name" content="KopfAI Blog" />
        <meta property="og:locale" content="pt_BR" />
        <meta property="article:published_time" content={publishedDate} />
        <meta property="article:modified_time" content={modifiedDate} />
        <meta property="article:section" content={post.category} />
        <meta property="article:tag" content={post.keywords.join(', ')} />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={postUrl} />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.meta_description} />
        <meta name="twitter:image" content={coverImage} />

        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbJsonLd)}
        </script>
        {faqJsonLd && (
          <script type="application/ld+json">
            {JSON.stringify(faqJsonLd)}
          </script>
        )}
        {howToJsonLd && (
          <script type="application/ld+json">
            {JSON.stringify(howToJsonLd)}
          </script>
        )}
      </Helmet>

      <ReadingProgress />

      <div className="min-h-screen bg-kopf-white pb-32">
        <BlogHeader />
        <PostHero 
          title={post.title} 
          category={post.category}
          coverImageUrl={post.cover_image_url}
        />
        
        <main className="container mx-auto px-4 max-w-4xl">
          <Breadcrumb className="mb-4 sm:mb-6">
            <BreadcrumbList className="flex-wrap">
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/" aria-label="Voltar para a página inicial do KopfAI Blog" className="flex items-center gap-1">
                    <Home className="w-4 h-4" aria-hidden="true" />
                    <span className="hidden sm:inline">Início</span>
                    <span className="sr-only sm:hidden">Início</span>
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem className="hidden sm:flex">
                <BreadcrumbLink className="text-kopf-text/60">
                  {post.category}
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="hidden sm:flex" />
              <BreadcrumbItem>
                <BreadcrumbPage className="text-kopf-primary line-clamp-1 max-w-[200px] sm:max-w-none text-sm sm:text-base">
                  {post.title}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <article>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8 pb-8 border-b border-kopf-secondary">
              <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-kopf-text/60">
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <time dateTime={post.created_at}>
                    {format(new Date(post.created_at), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                  </time>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  <span>{readingTime} min de leitura</span>
                </div>
                <div className="flex items-center gap-1">
                  <Eye className="w-4 h-4" />
                  <span>{post.views} {post.views === 1 ? 'visualização' : 'visualizações'}</span>
                </div>
              </div>
              
              <SocialShare 
                url={`/post/${post.slug}`}
                title={post.title}
                description={post.meta_description}
              />
            </div>

            <div 
              className="prose-kopf"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            <InlineCTA variant="default" postId={post.id} />

            <div className="mt-12 pt-8 border-t border-kopf-secondary">
              <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
                <div>
                  <h3 className="text-sm font-semibold text-kopf-text/60 mb-3">Tags:</h3>
                  <div className="flex flex-wrap gap-2">
                    {post.keywords.map((keyword) => (
                      <Badge key={keyword} variant="outline" className="border-kopf-secondary text-kopf-text">
                        {keyword}
                      </Badge>
                    ))}
                  </div>
                </div>
                <SocialShare 
                  url={`/post/${post.slug}`}
                  title={post.title}
                  description={post.meta_description}
                />
              </div>
            </div>

            <RelatedPosts currentPostId={post.id} category={post.category} keywords={post.keywords} />
          </article>
        </main>

        <CTABanner />
      </div>
    </>
  );
}
